import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useFirebase } from "../../context/FirebaseContext";
import "./AdminLayout.css";
import { useStoreSettings } from "../../hooks/useStoreSettings";
import { useLanguage } from "../../context/LanguageContext";
import { DEFAULT_STORE_ID } from "../../firebase/firebase";
const AdminLayout = () => {
  // Initialize sidebar state - closed on mobile, open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const { logout } = useFirebase();
  const navigate = useNavigate();
  const location = useLocation();
  const { storeSettings, loading } = useStoreSettings(DEFAULT_STORE_ID);
  const { language, switchLanguage } = useLanguage();

  // Translations for the admin interface
  const translations = {
    en: {
      dashboard: "Dashboard",

      orders: "Orders",
      products: "Products",
      categories: "Categories",
      customers: "Customers",
      settings: "Settings",
      administrator: "Administrator",
      logout: "Logout",
    },
    ar: {
      dashboard: "لوحة التحكم",
      orders: "الطلبات",
      products: "المنتجات",
      categories: "الفئات",
      customers: "العملاء",
      settings: "الإعدادات",
      administrator: "مدير",
      logout: "تسجيل الخروج",
    },
  };

  const t = translations[language] || translations.en;

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/admin/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleNavItemClick = () => {
    // Close sidebar on mobile when navigation item is clicked
    if (window.innerWidth <= 768) {
      setSidebarOpen(false);
    }
  };

  // Handle window resize to manage sidebar state
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Check if the current route is a login/register page
  const isAuthPage =
    location.pathname === "/admin/login" ||
    location.pathname === "/admin/register" ||
    location.pathname === "/admin/forgot-password";

  // Don't render the admin layout for auth pages
  if (isAuthPage) {
    return <Outlet />;
  }

  return (
    <div
      className={`admin-layout ${
        sidebarOpen ? "sidebar-open" : "sidebar-closed"
      } ${language === "ar" ? "rtl" : "ltr"}`}
    >
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}

      {/* Sidebar */}
      <div className="admin-sidebar">
        <div className="sidebar-header">
          <h2 className="sidebar-title">
            {storeSettings?.multilingualContent?.[language]?.name ||
              storeSettings?.name ||
              "Your Store Name"}
          </h2>
          <button className="sidebar-toggle" onClick={toggleSidebar}>
            {sidebarOpen ? "◀" : "▶"}
          </button>
        </div>

        <nav className="sidebar-nav">
          <ul>
            <li
              className={
                location.pathname === "/admin/dashboard" ? "active" : ""
              }
            >
              <Link to="/admin/dashboard" onClick={handleNavItemClick}>
                <span className="nav-icon">📊</span>
                <span className="nav-text">{t.dashboard}</span>
              </Link>
            </li>
            <li
              className={
                location.pathname.includes("/admin/orders") ? "active" : ""
              }
            >
              <Link to="/admin/orders" onClick={handleNavItemClick}>
                <span className="nav-icon">📦</span>
                <span className="nav-text">{t.orders}</span>
              </Link>
            </li>
            <li
              className={
                location.pathname.includes("/admin/products") ? "active" : ""
              }
            >
              <Link to="/admin/products" onClick={handleNavItemClick}>
                <span className="nav-icon">🏷️</span>
                <span className="nav-text">{t.products}</span>
              </Link>
            </li>
            <li
              className={
                location.pathname.includes("/admin/categories") ? "active" : ""
              }
            >
              <Link to="/admin/categories" onClick={handleNavItemClick}>
                <span className="nav-icon">📁</span>
                <span className="nav-text">{t.categories}</span>
              </Link>
            </li>
            <li
              className={
                location.pathname.includes("/admin/customers") ? "active" : ""
              }
            >
              <Link to="/admin/customers" onClick={handleNavItemClick}>
                <span className="nav-icon">👥</span>
                <span className="nav-text">{t.customers}</span>
              </Link>
            </li>
            <li
              className={
                location.pathname.includes("/admin/settings") ? "active" : ""
              }
            >
              <Link to="/admin/settings" onClick={handleNavItemClick}>
                <span className="nav-icon">⚙️</span>
                <span className="nav-text">{t.settings}</span>
              </Link>
            </li>
          </ul>
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {storeSettings?.email?.charAt(0).toUpperCase() || "U"}
            </div>
            <div className="user-details">
              <p className="user-name">{storeSettings?.ownerEmail}</p>
              <p className="user-role">{t.administrator}</p>
            </div>
          </div>
        </div>
        {/* Logout under Admin info */}
        <button className="logout-button" onClick={handleLogout}>
          <span className="logout-text">{t.logout}</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="admin-content">
        <header className="admin-header">
          <div className="header-left">
            <button className="mobile-sidebar-toggle" onClick={toggleSidebar}>
              ☰
            </button>
          </div>
          <div className="header-right">
            {/* Language Switcher */}
            <div className="language-switcher">
              <button
                className={`lang-btn ${language === "en" ? "active" : ""}`}
                onClick={() => switchLanguage("en")}
              >
                EN
              </button>
              <button
                className={`lang-btn ${language === "ar" ? "active" : ""}`}
                onClick={() => switchLanguage("ar")}
              >
                AR
              </button>
            </div>
          </div>
        </header>

        <main className="admin-main">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
